import { useMemo, useState } from 'react'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded'
import { CalendarPage } from '../Calendar/CalendarPage'
import { BoardExplorer } from '../BoardExplorer'
import { MeetingLayout } from '../Meetings/MeetingLayout'
import { MemberAnalysisView } from '../Analysis/mobile/MemberAnalysisView'
import type { AnalysisNavigate } from '../Analysis/tabs'
import { MemberBoard } from './MemberBoard'
import { useWorkspace } from '../../context/useWorkspace'
import type { TimelineBoardKey } from '../../types/schema'
import type { ChecklistItem } from '../../types/schema'
import type { TimelineElement } from '../../types/timelineElement'
import { useTimeline } from '../../context/useTimeline'
import { useAuth } from '../../context/useAuth'
import { useDisplayName } from '../../hooks/useDisplayName'
import { isChecklistGate } from '../../utils/checklistDependencyState'
import type { BlockerCtaSection } from '../../types/checklistBlockerCodes'
import type { ChecklistWorkflowStatus } from '../../types/schema'
import { ProofReviewActions } from '../ChecklistProof/ProofReviewActions'
import { EmptyHubState } from '../Common/EmptyHubState'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import { MemberNexusHero } from './controls/MemberNexusPrimitives'
import { useNls } from '../../nls/nlsProvider'
import { MEMBER_REVIEW_READINESS_LABEL, formatMemberReviewDate, getMemberReviewReadiness, getMemberReviewTimestamp } from './memberBoardUtils'

export type MemberBoardSubTab = 'STRIPBOARD' | 'MANAGE' | 'FOLDER' | 'MEETINGS' | 'ANALYSIS'

/**
 * Member mobile Board tab: hosts the six secondary surfaces. The secondary
 * tab row itself lives in the bottom bar (stacked above the primary tabs);
 * this component only renders the active surface.
 */
export const MemberBoardHub = ({
  sub,
  highlightedNodeId,
  onOpenElement,
  onOpenChecklistItem,
  onOpenBoardNode,
  onOpenChat,
  onNavigateAnalysisTarget,
}: {
  sub: MemberBoardSubTab
  highlightedNodeId: string | null
  onOpenElement: (element: TimelineElement) => void
  onOpenChecklistItem: (item: ChecklistItem, opts?: { pendingStatus?: ChecklistWorkflowStatus; targetSection?: BlockerCtaSection }) => void
  onOpenBoardNode: (elementId: string) => void
  onOpenChat: () => void
  onNavigateAnalysisTarget: AnalysisNavigate
}) => {
  const { t } = useNls()
  const { activeRole, activeWorkspaceId, canPerform, workspaces } = useWorkspace()
  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeWorkspaceId)
  const { checklistItems, elements } = useTimeline()
  const { currentUser, userProfile } = useAuth()
  const displayName = useDisplayName(userProfile ?? currentUser)
  const reviewer = currentUser ? { uid: currentUser.uid, displayName: displayName || currentUser.email || 'Reviewer' } : null
  const [folderBoard, setFolderBoard] = useState<TimelineBoardKey>('DASHBOARD')

  const parentById = useMemo(() => new Map(elements.map((element) => [element.id, element])), [elements])
  const reviewItems = useMemo(
    () =>
      checklistItems
        .filter((item) => !isChecklistGate(item))
        .filter((item) => item.workflowStatus === 'ASSESSING' || item.isPendingReview === true)
        .sort((left, right) => getMemberReviewTimestamp(right) - getMemberReviewTimestamp(left) || left.title.localeCompare(right.title))
        .slice(0, 24),
    [checklistItems],
  )
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null)
  const selectedReview = reviewItems.find((item) => item.id === selectedReviewId) ?? reviewItems[0] ?? null
  const readyCount = reviewItems.filter((item) => getMemberReviewReadiness(item) === 'READY').length
  const blockedCount = reviewItems.length - readyCount

  if (sub === 'STRIPBOARD') {
    return <MemberBoard highlightedNodeId={highlightedNodeId} onOpenElement={onOpenElement} onOpenChecklistItem={onOpenChecklistItem} />
  }

  if (sub === 'FOLDER') {
    const folderCountText = elements.length === 1 ? t('NLSMemberBoardFolderCountOne', { count: elements.length }) : t('NLSMemberBoardFolderCount', { count: elements.length })

    return (
      <div className="memberFolderStage" data-testid="member-board-folder">
        <MemberNexusHero eyebrow={t('NLSMemberBoardFolderEyebrow')} title={t('NLSMemberBoardFolderTitle')} rightSlot={<span className="memberBoardSectionCount">{folderCountText}</span>} />
        <BoardExplorer board={folderBoard} onBoardChange={setFolderBoard} onOpenChecklistItem={onOpenChecklistItem} />
      </div>
    )
  }

  if (sub === 'MANAGE') {
    const pendingText = reviewItems.length === 1 ? t('NLSMemberBoardReviewStatPendingOne', { count: reviewItems.length }) : t('NLSMemberBoardReviewStatPending', { count: reviewItems.length })
    const readyText = readyCount === 1 ? t('NLSMemberBoardReviewStatReadyOne', { count: readyCount }) : t('NLSMemberBoardReviewStatReady', { count: readyCount })
    const blockedText = blockedCount === 1 ? t('NLSMemberBoardReviewStatBlockedOne', { count: blockedCount }) : t('NLSMemberBoardReviewStatBlocked', { count: blockedCount })

    const pendingLabel = pendingText.replace(String(reviewItems.length), '').trim()
    const readyLabel = readyText.replace(String(readyCount), '').trim()
    const blockedLabel = blockedText.replace(String(blockedCount), '').trim()

    return (
      <section className="memberReviewConsole" aria-label={t('NLSMemberBoardReviewQueueAria')} data-testid="member-board-manage">
        <MemberNexusHero
          eyebrow={t('NLSMemberBoardReviewEyebrow')}
          title={t('NLSMemberBoardReviewTitle')}
          className="memberReviewHeader"
          rightSlot={
            <div className="memberReviewStats" aria-label={t('NLSMemberBoardReviewSummaryAria')}>
              <span aria-label={pendingText}>
                <strong>{reviewItems.length}</strong>
                <span aria-hidden="true">{pendingLabel}</span>
              </span>
              <span aria-label={readyText}>
                <strong>{readyCount}</strong>
                <span aria-hidden="true">{readyLabel}</span>
              </span>
              <span aria-label={blockedText}>
                <strong>{blockedCount}</strong>
                <span aria-hidden="true">{blockedLabel}</span>
              </span>
            </div>
          }
        />

        {reviewItems.length ? (
          <div className="memberReviewGrid">
            <div className="memberReviewQueue" aria-label={t('NLSMemberBoardReviewItemsAria')}>
              {reviewItems.map((item) => {
                const readiness = getMemberReviewReadiness(item)
                const parent = item.parentContainerId ? (parentById.get(item.parentContainerId) ?? null) : null
                const selected = selectedReview?.id === item.id
                return (
                  <Pressable key={item.id} className={`memberReviewRow ${selected ? 'isSelected' : ''}`} onClick={() => setSelectedReviewId(item.id)} aria-pressed={selected}>
                    <span className={`memberReviewSignal memberReviewSignal--${readiness.toLowerCase()}`} />
                    <span className="memberReviewRowMain">
                      <strong>{item.title}</strong>
                      <small>{parent?.title ?? 'Workspace checklist'}</small>
                    </span>
                    <span className={`memberReviewPill memberReviewPill--${readiness.toLowerCase()}`}>{MEMBER_REVIEW_READINESS_LABEL[readiness]}</span>
                  </Pressable>
                )
              })}
            </div>

            {selectedReview ? (
              <aside className="memberReviewInspector" aria-label={t('NLSMemberBoardReviewSelectedAria')}>
                <div>
                  <span className="memberEyebrow">Selected item</span>
                  <h3>{selectedReview.title}</h3>
                </div>

                <div className="memberReviewMetaGrid">
                  <span>
                    <PersonRoundedIcon fontSize="small" />
                    {selectedReview.assigneeName ?? 'Unassigned'}
                  </span>
                  <span>
                    <ScheduleRoundedIcon fontSize="small" />
                    {formatMemberReviewDate(selectedReview.scheduleEndDate ?? selectedReview.endDate)}
                  </span>
                  <span>
                    <AttachFileRoundedIcon fontSize="small" />
                    {selectedReview.requiresProofFile ? (selectedReview.proofFileUrl || selectedReview.proofSubmissionId ? 'Proof attached' : 'Proof needed') : 'No proof gate'}
                  </span>
                  <span>
                    <LocationOnRoundedIcon fontSize="small" />
                    {selectedReview.requiresLocationPin ? (selectedReview.verifiedCoordinates ? 'Location verified' : 'Location needed') : 'No location gate'}
                  </span>
                  <span>
                    <VerifiedUserRoundedIcon fontSize="small" />
                    {selectedReview.requiresCoSign ? (selectedReview.coSignedByName ?? 'Co-sign needed') : 'No co-sign gate'}
                  </span>
                </div>

                <div className="memberReviewTextBlock">
                  <span>Done when</span>
                  <p>{selectedReview.completionCriteria || 'No completion criteria recorded.'}</p>
                </div>

                {selectedReview.description ? (
                  <div className="memberReviewTextBlock">
                    <span>Context</span>
                    <p>{selectedReview.description}</p>
                  </div>
                ) : null}

                <ProofReviewActions item={selectedReview} actor={reviewer} onReviewed={() => setSelectedReviewId(null)} />

                <Button variant="primary" size="lg" onClick={() => onOpenChecklistItem(selectedReview)}>
                  <OpenInNewRoundedIcon fontSize="small" />
                  <span>Open details</span>
                </Button>
              </aside>
            ) : null}
          </div>
        ) : (
          <EmptyHubState preset="reviewQueueClear" appearance="card" className="memberReviewEmpty" />
        )}
      </section>
    )
  }

  if (sub === 'MEETINGS') {
    return (
      <MeetingLayout
        variant="member"
        isMobile
        allowScheduling={canPerform('meetings.schedule')}
        allowManaging={canPerform('meetings.manage')}
        allowJoining={activeRole === 'HELPER' || canPerform('meetings.join')}
        onOpenChat={onOpenChat}
        onOpenDocs={() => undefined}
      />
    )
  }

  if (sub === 'ANALYSIS') {
    return activeWorkspace ? (
      <MemberAnalysisView workspace={activeWorkspace} role={activeRole} elements={elements} canViewFullAnalysis={canPerform('workspace.view_analysis')} onNavigate={onNavigateAnalysisTarget} />
    ) : (
      <EmptyHubState preset="workspaceRequired" appearance="card" />
    )
  }

  return <CalendarPage activeBoard="DASHBOARD" readOnly onOpenElement={(elementId) => onOpenBoardNode(elementId)} onOpenChecklistItem={(parentElementId) => onOpenBoardNode(parentElementId)} />
}
